// A card's notes (docs/design/actions.md, A refined): one block under the card's content. Its head
// row folds the thread (▸, the avatars, "Notes · 9"; the newest note on one line while folded) and
// has ⧉ to read the whole thread in a panel. Over 4 entries the newest 3 show, with "Show all"
// above them where the older ones unfold. One toggle in the toolbar folds or opens the notes on
// every card (NotesContext); a card's own toggle flips that card until the toolbar is pressed again.

import { fmtDate, VIA_LABEL, type BoardItem, type BoardOrigin, type ReactionKind } from "@boem/core/shared";
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { NotesFilter } from "../filters.ts";
import { AiChip, Avatar, Linkify } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { readOrgSetting, writeOrgSetting } from "../org.ts";

/** Where an item or note came from: who said it, who filed it, and how it arrived. */
export function Origin({ o, compact }: { o: BoardOrigin; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  // sent or created from someone's personal board (orgs.md 6.1): who, and when; no link back, nothing else from that board
  if (o.via === "personal_board")
    return (
      <div className={`origin from-pb${compact ? " compact" : ""}`}>
        <span className="src">{VIA_LABEL[o.via]}</span>
        <span>
          From <b>{o.said ?? o.filedBy}</b>&rsquo;s personal board
          {!compact ? ` · sent ${fmtDate(o.at)}` : ""}
        </span>
      </div>
    );
  return (
    <div className={`origin${compact ? " compact" : ""}`}>
      <span className="src">{VIA_LABEL[o.via] ?? o.via}</span>
      <span>
        said by <b>{o.said ?? "someone"}</b>
        {o.place ? ` in ${o.place}` : ""}
        {!compact && o.sentAt ? ` · ${fmtDate(o.sentAt)}` : ""}
      </span>
      {!compact && (
        <span>
          &middot; filed by <b>{o.filedBy}</b>
        </span>
      )}
      {o.aiDrafted && <span className="ai">Claude-drafted</span>}
      {/* a note from GitHub is the comment itself (it follows edits there): nothing more to show */}
      {o.via !== "github" && (
        <button type="button" className="xref" onClick={() => setOpen(!open)}>
          {open ? "hide original" : "original message"}
        </button>
      )}
      {o.url && (
        <a href={o.url} target="_blank" rel="noopener noreferrer">
          {o.via === "github" ? "open on GitHub" : "open in Teams"} &#8599;
        </a>
      )}
      {open && <div className="origin-text">{o.text || "(no text)"}</div>}
    </div>
  );
}

export interface Review {
  name: string;
  /** someone's AI: shown under their name with an AI chip, kept apart from what they did themselves */
  agent: BoardItem["notes"][number]["agent"];
  kind: ReactionKind | null;
  at: string;
  notes: BoardItem["notes"];
}

/** One entry per person (and per person's AI app) who reacted or wrote a note, oldest first. */
export function reviewsOf(item: Pick<BoardItem, "reactions" | "notes">): Review[] {
  const by = new Map<string, Review>();
  const key = (name: string, agent: Review["agent"]) => `${name}|${agent?.app ?? ""}`;
  for (const r of item.reactions) by.set(key(r.name, r.agent), { name: r.name, agent: r.agent, kind: r.kind, at: r.at, notes: [] });
  for (const n of item.notes) {
    const k = key(n.name, n.agent);
    const cur = by.get(k) ?? { name: n.name, agent: n.agent, kind: null, at: n.at, notes: [] };
    cur.notes.push(n);
    by.set(k, cur);
  }
  return [...by.values()].sort((a, b) => a.at.localeCompare(b.at));
}

/** One line of the thread: a note, or a reaction from someone who wrote none. */
export interface ThreadRow {
  r: Review;
  note: BoardItem["notes"][number] | null;
  at: string;
  /** the reaction tag rides on each person's first row */
  tag: ReactionKind | null;
}

/** The thread, oldest first, as the Notes filter lets it show (everyone, people only, AI only). */
export function threadOf(item: Pick<BoardItem, "reactions" | "notes">, filter: NotesFilter = "all"): { reviews: Review[]; rows: ThreadRow[]; notes: number } {
  const reviews = reviewsOf(item).filter((r) => filter === "all" || (filter === "ai") === !!r.agent);
  const rows = reviews
    .flatMap((r): Omit<ThreadRow, "tag">[] => (r.notes.length ? r.notes.map((n) => ({ r, note: n, at: n.at })) : [{ r, note: null, at: r.at }]))
    .sort((a, b) => a.at.localeCompare(b.at));
  const tagged = new Set<Review>();
  const withTags = rows.map((row): ThreadRow => {
    const tag = row.r.kind && !tagged.has(row.r) ? row.r.kind : null;
    if (tag) tagged.add(row.r);
    return { ...row, tag };
  });
  return { reviews, rows: withTags, notes: reviews.reduce((n, r) => n + r.notes.length, 0) };
}

/** Over this many entries a thread shows its newest few in place, with "Show all" above them. */
export const LONG_THREAD = 4;
export const NEWEST = 3;

/** The screenshots added with a note, shown under it (the card's and the panel's ShotStrip for that note). */
export type NoteShots = (noteId: string) => ReactNode;

/** `onEdit`: your own note gets an Edit (the Notes dialog, with it open for editing). `shots`: the note's screenshots. */
export function NoteRow({ row, full = false, onEdit, shots }: { row: ThreadRow; full?: boolean; onEdit?: (noteId: string) => void; shots?: NoteShots }) {
  const { r, note, at, tag } = row;
  return (
    <div className={`note${r.agent ? " note-ai" : ""}`}>
      <Avatar name={r.name} ai={!!r.agent} />
      <div className="note-main">
        <div className="meta">
          <b>{r.name}</b>
          {r.agent && (
            <>
              {" "}
              <AiChip agent={r.agent} />
            </>
          )}
          {tag && (
            <>
              {" "}
              <span className={`r-tag ${tag}`}>{tag}</span>
            </>
          )}
          {at ? ` · ${fmtDate(at)}` : ""}
          {note?.mine && onEdit && (
            <button type="button" className="note-edit" aria-label={`Edit your note of ${fmtDate(note.at)}`} title="Edit your note (the history keeps what it said)" onClick={() => onEdit(note.id)}>
              Edit
            </button>
          )}
        </div>
        {note && (
          <div className="body">
            <Linkify text={note.body} />
          </div>
        )}
        {note && shots?.(note.id)}
        {note?.origin && <Origin o={note.origin} compact={!full} />}
      </div>
    </div>
  );
}

// -- Notes on every card ------------------------------------------------------------------

/**
 * The toolbar's "notes on every card": shown (`all`) or folded. `gen` counts presses: a card's own
 * flip is kept only for the press it was made under, so pressing the toolbar again sets every card
 * to the new state. `flipped` is how many cards differ now (a dot on the toolbar toggle).
 */
export interface NotesView {
  all: boolean;
  gen: number;
  flipped: number;
  toggle: () => void;
  /** a card says whether it differs from the toolbar now (`id` is the card's own) */
  flip: (id: string, differs: boolean) => void;
}
export const NotesContext = createContext<NotesView>({ all: true, gen: 0, flipped: 0, toggle: () => {}, flip: () => {} });

/** The toolbar's state for one person, remembered on this device (localStorage, per person). */
export function useNotesView(person: string | null): NotesView {
  // per person and per board (org.ts; OEMUAP picks up the setting from before boards)
  const key = person ? `buyoem.notesAll.${person}` : null;
  const [all, setAll] = useState(true);
  const [gen, setGen] = useState(0);
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (!key) return;
    setAll(readOrgSetting(key) !== "0");
  }, [key]);
  const toggle = useCallback(() => {
    setAll((cur) => {
      if (key) writeOrgSetting(key, cur ? "0" : "1");
      return !cur;
    });
    setGen((g) => g + 1);
    setFlipped(new Set());
  }, [key]);
  const flip = useCallback(
    (id: string, differs: boolean) =>
      setFlipped((cur) => {
        if (cur.has(id) === differs) return cur;
        const next = new Set(cur);
        if (differs) next.add(id);
        else next.delete(id);
        return next;
      }),
    [],
  );
  return useMemo(() => ({ all, gen, flipped: flipped.size, toggle, flip }), [all, gen, flipped, toggle, flip]);
}

/** A card's own flip, and the press it was made under. */
export interface OwnFlip {
  gen: number;
  open: boolean;
}

/** What a card shows by default: the toolbar's state, except a card opened in place, which starts folded. */
export const notesDefault = (view: Pick<NotesView, "all">, nested: boolean) => (nested ? false : view.all);

/** Whether a card's thread is open: its own flip while the toolbar hasn't been pressed since, else the default. */
export const threadOpen = (view: Pick<NotesView, "all" | "gen">, own: OwnFlip | null, nested: boolean) => (own && own.gen === view.gen ? own.open : notesDefault(view, nested));

/** "Jake · Sep 30, 2026 · 09/30 - Brought it up…": the newest entry on one line, for a folded thread. */
function Newest({ row }: { row: ThreadRow }) {
  return (
    <span className="notes-last" title={row.note?.body}>
      <b>{row.r.name}</b>
      {row.r.agent ? ` (${row.r.agent.app})` : ""} &middot; {fmtDate(row.at)}
      {row.note ? <> &middot; {row.note.body}</> : row.r.kind ? <> &middot; {row.r.kind}</> : null}
    </span>
  );
}

/**
 * The notes block on a card. Nothing when nobody has reacted or noted. `open`/`onToggle` are the
 * card's (NotesContext, its own flip); `onPanel` opens the thread in a panel (left out on a card
 * opened in place, as its row has ⧉ already).
 */
export function NotesBlock({
  item,
  filter = "all",
  open,
  onToggle,
  onPanel,
  onEdit,
  label,
  shots,
}: {
  item: BoardItem;
  filter?: NotesFilter;
  open: boolean;
  onToggle: () => void;
  onPanel?: () => void;
  /** your own notes get an Edit */
  onEdit?: (noteId: string) => void;
  /** how the card names itself: "03-01" */
  label: string;
  /** each note's screenshots, under it */
  shots?: NoteShots;
}) {
  const [all, setAll] = useState(false);
  const { reviews, rows, notes } = threadOf(item, filter);
  if (!reviews.length) return null;
  const long = rows.length > LONG_THREAD;
  const shown = long && !all ? rows.slice(-NEWEST) : rows;
  const what = notes ? `Notes · ${notes}` : "Reviewed";
  return (
    <div className="notes-block" data-open={open ? "1" : undefined}>
      <div className="notes-head">
        <button
          type="button"
          className="notes-tog"
          aria-expanded={open}
          title={`${open ? "Hide" : "Show"} the notes on this card (${reviews.length} reviewer${reviews.length > 1 ? "s" : ""})`}
          onClick={onToggle}
        >
          <Icon name="chevron" />
          <span className="avs">
            {reviews.map((r) => (
              <Avatar key={`${r.name}|${r.agent?.app ?? ""}`} name={r.name} ai={!!r.agent} />
            ))}
          </span>
          <span className="notes-lbl">{what}</span>
        </button>
        {!open && rows.length > 0 && <Newest row={rows[rows.length - 1]!} />}
        {onPanel && (
          <button type="button" className="sub-act notes-panel" aria-label={`Open the notes on ${label} in a panel`} title="Open the notes in a panel to the right" onClick={onPanel}>
            <Icon name="panel" />
          </button>
        )}
      </div>
      {open && (
        <div className="notes">
          {long && (
            <button type="button" className="notes-more" aria-expanded={all} onClick={() => setAll(!all)}>
              {all ? `Newest ${NEWEST} only` : `Show all ${rows.length}`} <Icon name="caret" />
              {!all && <span className="h">{rows.length - NEWEST} earlier</span>}
            </button>
          )}
          {shown.map((row) => (
            <NoteRow key={row.note?.id ?? `rx-${row.r.name}|${row.r.agent?.app ?? ""}`} row={row} onEdit={onEdit} shots={shots} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A notes panel's body: the whole thread, origins in full, and the composer held at the bottom edge. */
export function NotesThread({
  item,
  filter = "all",
  onCompose,
  onEdit,
  footer,
  shots,
}: {
  item: BoardItem;
  filter?: NotesFilter;
  onCompose?: () => void;
  /** your own notes get an Edit */
  onEdit?: (noteId: string) => void;
  footer?: ReactNode;
  /** each note's screenshots, under it */
  shots?: NoteShots;
}) {
  const { rows } = threadOf(item, filter);
  return (
    <>
      <div className="sp-body np-body">
        {rows.length ? (
          <div className="notes">
            {rows.map((row) => (
              <NoteRow key={row.note?.id ?? `rx-${row.r.name}|${row.r.agent?.app ?? ""}`} row={row} full onEdit={onEdit} shots={shots} />
            ))}
          </div>
        ) : (
          <div className="empty">{item.notes.length ? "No notes match the Notes filter." : "No notes yet."}</div>
        )}
        {footer}
      </div>
      {onCompose && (
        <div className="np-compose">
          <button type="button" className="compose" aria-label={`Add a note to #${item.num}`} onClick={onCompose}>
            <Icon name="note" />
            <span className="lg">Add a note&hellip;</span>
          </button>
        </div>
      )}
    </>
  );
}

/** "4 people · 2 from AI apps · newest Oct 2, 2026": a notes panel's meta line. */
export function threadMeta(item: BoardItem, filter: NotesFilter = "all"): string[] {
  const { reviews, rows } = threadOf(item, filter);
  const people = new Set(reviews.filter((r) => !r.agent).map((r) => r.name)).size;
  const ai = item.notes.filter((n) => n.agent).length;
  const newest = rows[rows.length - 1]?.at;
  return [
    `${people} ${people === 1 ? "person" : "people"}`,
    ...(ai && filter !== "people" ? [`${ai} from AI apps`] : []),
    ...(newest ? [`newest ${fmtDate(newest)}`] : []),
    filter === "all" ? "everyone's notes" : filter === "people" ? "people's notes only (the Notes filter)" : "AI notes only (the Notes filter)",
  ];
}
